import { useState, useEffect } from 'react';
import { Settings, Save } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import TelegramChannelsSection from '../components/settings/TelegramChannelsSection.jsx';
import DCATargetsSection from '../components/settings/DCATargetsSection.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const RISK_LABELS = { 1: 'Ultra Conservative', 2: 'Very Conservative', 3: 'Conservative', 4: 'Moderate-Conservative', 5: 'Moderate', 6: 'Moderate-Aggressive', 7: 'Aggressive', 8: 'Very Aggressive', 9: 'Ultra Aggressive', 10: 'Maximum Risk' };
const HORIZONS = ['day', 'swing', 'position', 'long_term'];
const HORIZON_LABELS = { day: 'Day Trading', swing: 'Swing (days-weeks)', position: 'Position (weeks-months)', long_term: 'Long-term (months-years)' };

const ALL_SECTORS = ['AI/Semiconductors', 'Tech/Software', 'Data Centers/Power', 'Energy', 'Defense', 'Financials', 'Materials', 'Biotech/Pharma', 'EV/Auto', 'Space', 'Consumer', 'Crypto', 'Futures', 'Crypto Direct'];

function Field({ label, children }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-korinth-border/20">
      <span className="text-[11px] font-mono text-korinth-muted">{label}</span>
      <div>{children}</div>
    </div>
  );
}

export default function SettingsPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('settings'); }, []);

  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const pRes = await fetch('/api/profile'); if (pRes.ok) setProfile(await pRes.json());
      } catch {}
    }
    load();
  }, []);

  async function saveProfile() {
    setSaving(true);
    try {
      await fetch('/api/profile', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile) });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch {} finally { setSaving(false); }
  }

  function updateField(field, value) { setProfile(p => ({ ...p, [field]: value })); }
  function toggleArrayItem(field, item) {
    const arr = profile[field] || [];
    const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
    updateField(field, next);
  }

  if (!profile) return <div className="text-korinth-muted font-mono text-center py-12">Loading profile...</div>;

  // Sprint 21.6g: Holdings display computed from PLUTUS exposure on /holdings.
  // Manual Holdings form removed from Settings — PLUTUS auto-syncs everything.
  const availableCapital = profile.total_investable_capital
    ? profile.total_investable_capital - (profile.total_investable_capital * profile.cash_reserve_pct / 100)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-korinth-accent" />
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <span className="text-korinth-muted text-sm">Investor profile + constraints</span>
        </div>
        <button onClick={saveProfile} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 rounded bg-korinth-accent text-black text-xs font-mono font-bold hover:bg-korinth-accent/90 disabled:opacity-50">
          <Save className="w-3 h-3" /> {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Profile */}
        <ModuleContainer title="Risk Profile">
          <div className="space-y-1">
            <Field label="Risk Tolerance">
              <div className="flex items-center gap-2">
                <input type="range" min="1" max="10" value={profile.risk_tolerance} onChange={e => updateField('risk_tolerance', parseInt(e.target.value))}
                  className="w-32 accent-korinth-accent" />
                <span className="text-xs font-mono font-bold text-white w-6 text-right">{profile.risk_tolerance}</span>
                <span className="text-[9px] font-mono text-korinth-accent">{RISK_LABELS[profile.risk_tolerance]}</span>
              </div>
            </Field>
            <Field label="Max Single Position %">
              <input type="number" value={profile.max_single_position_pct} onChange={e => updateField('max_single_position_pct', parseFloat(e.target.value))}
                className="w-16 bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white text-right" />
            </Field>
            <Field label="Max Sector Exposure %">
              <input type="number" value={profile.max_sector_exposure_pct} onChange={e => updateField('max_sector_exposure_pct', parseFloat(e.target.value))}
                className="w-16 bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white text-right" />
            </Field>
          </div>
        </ModuleContainer>

        {/* Capital */}
        <ModuleContainer title="Capital">
          <div className="space-y-1">
            <Field label="Total Investable Capital ($)">
              <input type="number" value={profile.total_investable_capital || ''} placeholder="e.g. 500000"
                onChange={e => updateField('total_investable_capital', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-32 bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white text-right" />
            </Field>
            <Field label="Cash Reserve %">
              <input type="number" value={profile.cash_reserve_pct} onChange={e => updateField('cash_reserve_pct', parseFloat(e.target.value))}
                className="w-16 bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white text-right" />
            </Field>
            {profile.total_investable_capital && (
              <>
                <Field label="Max Position Size">
                  <span className="text-xs font-mono text-white">${(profile.total_investable_capital * profile.max_single_position_pct / 100).toLocaleString()}</span>
                </Field>
                <Field label="Live Holdings">
                  <a href="/holdings" className="text-xs font-mono text-korinth-accent hover:underline">See PLUTUS /holdings →</a>
                </Field>
              </>
            )}
          </div>
        </ModuleContainer>

        {/* Time Horizon */}
        <ModuleContainer title="Time Horizon">
          <div className="flex gap-2 flex-wrap">
            {HORIZONS.map(h => (
              <button key={h} onClick={() => updateField('primary_horizon', h)}
                className={`px-3 py-1.5 rounded text-[10px] font-mono border transition-colors ${profile.primary_horizon === h ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
                {HORIZON_LABELS[h]}
              </button>
            ))}
          </div>
        </ModuleContainer>

        {/* Constraints */}
        <ModuleContainer title="Constraints">
          <div className="space-y-2">
            {[
              ['no_leverage', 'No leveraged products'],
              ['no_crypto', 'No crypto'],
              ['no_short_selling', 'Long only (no shorts)'],
              ['tax_sensitive', 'Tax sensitive (prefer long-term holds)'],
              ['prefers_etfs', 'Prefer ETFs over individual stocks'],
              ['prefers_options', 'Include options strategies'],
              ['dividend_focus', 'Prioritize dividend payers'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!profile[key]} onChange={() => updateField(key, !profile[key])}
                  className="accent-korinth-accent" />
                <span className="text-[11px] font-mono text-korinth-text">{label}</span>
              </label>
            ))}
          </div>
        </ModuleContainer>
      </div>

      {/* Sector Preferences */}
      <ModuleContainer title="Sector Preferences">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-[10px] font-mono text-korinth-accent uppercase tracking-wider mb-2">Preferred Sectors</h4>
            <div className="flex gap-1.5 flex-wrap">
              {ALL_SECTORS.map(s => (
                <button key={s} onClick={() => toggleArrayItem('preferred_sectors', s)}
                  className={`px-2 py-1 rounded text-[9px] font-mono border transition-colors ${(profile.preferred_sectors || []).includes(s) ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-[10px] font-mono text-korinth-danger uppercase tracking-wider mb-2">Excluded Sectors</h4>
            <div className="flex gap-1.5 flex-wrap">
              {ALL_SECTORS.map(s => (
                <button key={s} onClick={() => toggleArrayItem('excluded_sectors', s)}
                  className={`px-2 py-1 rounded text-[9px] font-mono border transition-colors ${(profile.excluded_sectors || []).includes(s) ? 'bg-korinth-danger/10 border-korinth-danger text-korinth-danger' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </ModuleContainer>

      {/* Sprint 21.6g: Holdings manual form REMOVED. PLUTUS auto-syncs everything.
          Status + Sync Health moved to /holdings page footer. */}
      <PlutusSectionSlim />

      {/* Sprint 21.7.3: Telegram & Notifications */}
      <TelegramNotificationsSection />

      {/* Sprint 21.7: Asset Taxonomy Health */}
      <AssetTaxonomyHealthSection />

      {/* Sprint 19: Watchlist Preferences */}
      <WatchlistPreferencesSection />

      {/* Sprint 18: DCA targets + Telegram channel management */}
      <DCATargetsSection />
      <TelegramChannelsSection />
    </div>
  );
}

// Sprint 21.7.3: Telegram & Notifications section — severity tier toggles,
// morning brief + weekly digest timing, quiet hours, timezone.
const WEEKDAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const COMMON_TIMEZONES = [
  'Africa/Cairo', 'Asia/Dubai', 'Europe/London', 'America/New_York',
  'America/Los_Angeles', 'Asia/Singapore', 'Asia/Tokyo', 'UTC',
];
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    }
  }
  return out;
})();

function TelegramNotificationsSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  async function load() {
    try {
      const r = await fetch('/api/user/notification-config');
      if (r.ok) setCfg(await r.json());
    } catch (e) { setError(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function save(updates) {
    setSaving(true); setError(null);
    try {
      const r = await fetch('/api/user/notification-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setCfg(await r.json());
      setSavedAt(Date.now());
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  if (!cfg) return null;

  return (
    <ModuleContainer title="Telegram & Notifications" subtitle="Severity tiers · morning brief · weekly digest · quiet hours">
      <div className="space-y-4">
        {/* Severity tier push toggles */}
        <div>
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Severity tier push (controls which alerts go to Telegram)</div>
          <div className="space-y-1.5">
            <ToggleRow
              label="🚨 Critical alerts"
              sub="High-conviction, portfolio-touching, event-driven"
              checked={cfg.severity_critical_push}
              onChange={v => save({ severity_critical_push: v })}
              disabled={saving}
            />
            <ToggleRow
              label="🌅 Daily digest"
              sub={`Morning brief at ${cfg.morning_brief_time} ${cfg.user_timezone}`}
              checked={cfg.severity_daily_digest_push}
              onChange={v => save({ severity_daily_digest_push: v })}
              disabled={saving}
            />
            <ToggleRow
              label="📊 Weekly digest"
              sub={`${titleCase(cfg.weekly_digest_day)} ${cfg.weekly_digest_time} recap`}
              checked={cfg.severity_weekly_digest_push}
              onChange={v => save({ severity_weekly_digest_push: v })}
              disabled={saving}
            />
          </div>
        </div>

        {/* Morning brief */}
        <div>
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Morning brief</div>
          <div className="flex flex-wrap gap-3 items-center">
            <DropdownRow label="Time" value={cfg.morning_brief_time} options={TIME_OPTIONS}
              onChange={v => save({ morning_brief_time: v })} disabled={saving} />
            <DropdownRow label="Timezone" value={cfg.user_timezone}
              options={COMMON_TIMEZONES.includes(cfg.user_timezone) ? COMMON_TIMEZONES : [cfg.user_timezone, ...COMMON_TIMEZONES]}
              onChange={v => save({ user_timezone: v })} disabled={saving} />
          </div>
        </div>

        {/* Weekly digest */}
        <div>
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Weekly digest</div>
          <div className="flex flex-wrap gap-3 items-center">
            <DropdownRow label="Day" value={cfg.weekly_digest_day} options={WEEKDAYS}
              onChange={v => save({ weekly_digest_day: v })} disabled={saving} />
            <DropdownRow label="Time" value={cfg.weekly_digest_time} options={TIME_OPTIONS}
              onChange={v => save({ weekly_digest_time: v })} disabled={saving} />
          </div>
        </div>

        {/* Quiet hours */}
        <div>
          <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Quiet hours</div>
          <div className="space-y-1.5">
            <ToggleRow
              label="Enable quiet hours"
              sub={cfg.quiet_hours_enabled ? `From ${cfg.quiet_hours_start} to ${cfg.quiet_hours_end} ${cfg.user_timezone}` : 'Off'}
              checked={cfg.quiet_hours_enabled}
              onChange={v => save({ quiet_hours_enabled: v })}
              disabled={saving}
            />
            {cfg.quiet_hours_enabled && (
              <div className="flex flex-wrap gap-3 items-center pl-6">
                <DropdownRow label="From" value={cfg.quiet_hours_start} options={TIME_OPTIONS}
                  onChange={v => save({ quiet_hours_start: v })} disabled={saving} />
                <DropdownRow label="To" value={cfg.quiet_hours_end} options={TIME_OPTIONS}
                  onChange={v => save({ quiet_hours_end: v })} disabled={saving} />
              </div>
            )}
            <ToggleRow
              label="Allow NEMESIS emergencies to override quiet hours"
              sub="90-second emergency cycles break through"
              checked={cfg.nemesis_overrides_quiet}
              onChange={v => save({ nemesis_overrides_quiet: v })}
              disabled={saving}
            />
          </div>
        </div>

        {/* Status line */}
        <div className="flex items-center gap-3 text-[10px] font-mono">
          {saving && <span className="text-korinth-muted">saving…</span>}
          {!saving && savedAt && Date.now() - savedAt < 3000 && (
            <span className="text-korinth-accent">✓ saved</span>
          )}
          {error && <span className="text-korinth-danger">{error}</span>}
        </div>
      </div>
    </ModuleContainer>
  );
}

function ToggleRow({ label, sub, checked, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className="flex items-start gap-3 w-full text-left p-2 rounded hover:bg-korinth-bg/50 disabled:opacity-50"
      style={{ minHeight: 44 }}
    >
      <div className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-korinth-accent border-korinth-accent' : 'border-korinth-border'}`}>
        {checked && <span className="text-black text-[10px] font-bold">✓</span>}
      </div>
      <div>
        <div className="text-[11px] font-mono text-white">{label}</div>
        {sub && <div className="text-[10px] font-mono text-korinth-muted">{sub}</div>}
      </div>
    </button>
  );
}

function DropdownRow({ label, value, options, onChange, disabled }) {
  return (
    <label className="flex items-center gap-2 text-[10px] font-mono">
      <span className="text-korinth-muted uppercase tracking-wider">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-white disabled:opacity-50 min-h-[36px]">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function titleCase(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

// Sprint 21.7: Asset Taxonomy Health tile — coverage + refresh
function AssetTaxonomyHealthSection() {
  const [health, setHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  async function load() {
    try { const r = await fetch('/api/taxonomy/health'); if (r.ok) setHealth(await r.json()); } catch {}
  }
  useEffect(() => { load(); }, []);

  async function refresh() {
    setRefreshing(true); setRefreshMsg(null);
    try {
      const r = await fetch('/api/taxonomy/refresh', { method: 'POST' });
      const d = r.ok ? await r.json() : null;
      if (d) setRefreshMsg(`Classified ${d.ok}/${d.total} tickers (${d.failed} failed)`);
      await load();
    } catch (e) { setRefreshMsg(`Error: ${e.message}`); }
    setRefreshing(false);
  }

  if (!health) return null;
  const bySrc = (health.by_source || []).map(s => `${s.source}:${s.cnt}`).join(' · ');
  const lastRefresh = health.last_refresh ? new Date(health.last_refresh).toLocaleString() : 'never';

  return (
    <ModuleContainer title="Asset Taxonomy Health" subtitle="GICS sector + themes + geography coverage for Kamal's universe">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase">Classified</div>
          <div className="text-lg font-mono font-bold text-white">{health.total_classified}</div>
          <div className="text-[9px] font-mono text-korinth-muted">{health.with_sector} with sector · {health.with_themes} with themes</div>
        </div>
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase">Missing</div>
          <div className="text-lg font-mono font-bold" style={{ color: health.missing_count > 0 ? '#f59e0b' : '#10b981' }}>{health.missing_count}</div>
          <div className="text-[9px] font-mono text-korinth-muted truncate" title={(health.missing_tickers || []).join(', ')}>
            {(health.missing_tickers || []).slice(0, 5).join(', ') || 'none'}
          </div>
        </div>
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase">Stale (&gt;90d)</div>
          <div className="text-lg font-mono font-bold text-white">{health.stale_count}</div>
        </div>
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase">Last refresh</div>
          <div className="text-[10px] font-mono text-white">{lastRefresh}</div>
        </div>
      </div>

      <div className="text-[10px] font-mono text-korinth-muted mb-3">Sources: {bySrc || 'none'}</div>

      <div className="flex items-center gap-3">
        <button onClick={refresh} disabled={refreshing}
          className="text-[10px] font-mono px-3 py-1.5 rounded bg-korinth-accent/10 border border-korinth-accent/40 text-korinth-accent font-bold hover:bg-korinth-accent/20 disabled:opacity-50">
          {refreshing ? 'Refreshing...' : 'Refresh now'}
        </button>
        {refreshMsg && <span className="text-[10px] font-mono text-korinth-text">{refreshMsg}</span>}
      </div>
    </ModuleContainer>
  );
}

function WatchlistPreferencesSection() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch('/api/watchlist/user');
        if (r.ok) { const j = await r.json(); setSummary(j.summary); }
      } catch {}
    }
    load();
  }, []);

  if (!summary) return null;

  return (
    <ModuleContainer title="Watchlist Preferences" subtitle="Your curated book drives personalized KAIROS coverage">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Core Holdings</div>
          <div className="text-2xl font-mono font-bold text-korinth-accent">{summary.core_count || 0}</div>
          <div className="text-[9px] font-mono text-korinth-muted">Priority in briefings · full KAIROS anchoring</div>
        </div>
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Active Tier</div>
          <div className="text-2xl font-mono font-bold text-blue-400">{summary.active_count || 0}</div>
          <div className="text-[9px] font-mono text-korinth-muted">Short/swing coverage · per-ticker news</div>
        </div>
        <div className="bg-korinth-bg rounded p-3">
          <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Monitor</div>
          <div className="text-2xl font-mono font-bold text-korinth-muted">{summary.monitor_count || 0}</div>
          <div className="text-[9px] font-mono text-korinth-muted">Headline capture only · no Claude spend</div>
        </div>
      </div>
      <div className="text-[10px] font-mono text-korinth-muted leading-relaxed mb-3">
        Ticker news fetches every 15 min for Core + Active tier. Polymarket odds refresh daily at 03:30 UTC. Manage tiers and add/remove tickers on the <a href="/watchlist" className="text-korinth-accent hover:underline">Watchlist page</a>.
      </div>
      <a href="/watchlist" className="inline-flex items-center gap-1 text-[10px] font-mono px-3 py-1.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
        Manage Watchlist →
      </a>
    </ModuleContainer>
  );
}

// Sprint 21.6g: Slim PLUTUS section — dropped status tiles (moved to /holdings
// as "Sync Health" footer). What belongs in Settings is configuration:
// account purposes + the unclassified-purpose reminder banner. Live sync state,
// Sync Now buttons, and per-account NAV live on /holdings where they belong.
function PlutusSectionSlim() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    async function load() {
      try { const r = await fetch('/api/plutus/status'); if (r.ok) setStatus(await r.json()); } catch {}
    }
    load();
  }, []);

  return (
    <ModuleContainer title="Private Holdings Layer (PLUTUS)" subtitle="Account classification · live sync status lives on /holdings">
      {status && !status.encryption_configured && (
        <div className="bg-korinth-danger/10 border border-korinth-danger/30 rounded px-3 py-2 text-[11px] font-mono text-korinth-danger mb-3">
          ⚠️ ENCRYPTION_KEY not set in backend env. PLUTUS will not sync until configured.
        </div>
      )}

      {/* Per-account purpose dropdown + config-reminders banner (Sprint 21.6d/e) */}
      <PlutusAccountsPurposeEditor />

      <div className="flex items-center gap-3 mt-3">
        <a href="/holdings" className="inline-flex items-center gap-1 text-[10px] font-mono px-3 py-1.5 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
          Open /holdings (PIN required) →
        </a>
        {status && (
          <span className="text-[10px] font-mono text-korinth-muted">
            PIN: <span className={status.pin_set ? 'text-korinth-accent' : 'text-korinth-warning'}>{status.pin_set ? 'set' : 'not yet set'}</span>
          </span>
        )}
      </div>

      <div className="text-[9px] font-mono text-korinth-muted/70 leading-relaxed mt-3 pt-3 border-t border-korinth-border/30">
        P17 · Dollar-valued fields are AES-256-GCM encrypted at rest. KAIROS receives only percentages + sector breakdown via portfolio_exposure_summary. Telegram alerts never contain dollar amounts. See docs/ARCHITECTURE.md.
      </div>
    </ModuleContainer>
  );
}

function PlutusAccountsPurposeEditor() {
  const [accounts, setAccounts] = useState(null);
  const [saving, setSaving] = useState(null);
  const [locked, setLocked] = useState(false);
  const [reminder, setReminder] = useState(null);

  async function load() {
    try {
      const [ovRes, cfgRes] = await Promise.all([
        fetch('/api/plutus/overview?window=30d'),
        fetch('/api/plutus/config-reminders'),
      ]);
      if (ovRes.status === 423) { setLocked(true); return; }
      setLocked(false);
      if (ovRes.ok) { const d = await ovRes.json(); setAccounts(d.accounts || []); }
      if (cfgRes.ok) { const d = await cfgRes.json(); setReminder(d?.message ? d : null); }
    } catch {}
  }

  useEffect(() => { load(); }, []);

  async function updatePurpose(accountId, purpose) {
    setSaving(accountId);
    try {
      const r = await fetch(`/api/plutus/accounts/${accountId}/purpose`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose }),
      });
      if (r.status === 423) { setLocked(true); window.location.href = '/holdings'; return; }
      if (r.ok) await load();
    } catch {}
    setSaving(null);
  }

  const PURPOSE_OPTIONS = [
    { value: 'discretionary', label: 'Discretionary' },
    { value: 'algo_paper',    label: 'Algo (paper)' },
    { value: 'algo_live',     label: 'Algo (live)' },
    { value: 'crypto_spot',   label: 'Crypto Spot' },
    { value: 'retirement',    label: 'Retirement' },
    { value: 'unclassified',  label: 'Unclassified' },
  ];

  if (locked) {
    return (
      <div className="mt-4 pt-3 border-t border-korinth-border/30">
        <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Account Purposes</div>
        <div className="text-[11px] font-mono text-korinth-muted italic">PLUTUS locked — unlock via <a href="/holdings" className="text-korinth-accent hover:underline">/holdings</a> to manage account purposes.</div>
      </div>
    );
  }

  if (!accounts) return null;
  if (accounts.length === 0) {
    return (
      <div className="mt-4 pt-3 border-t border-korinth-border/30">
        <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Account Purposes</div>
        <div className="text-[11px] font-mono text-korinth-muted italic">No accounts yet — connect IBKR or Binance and trigger a sync first.</div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-3 border-t border-korinth-border/30">
      <div className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Account Purposes</div>

      {/* Sprint 21.6e: config reminder banner — moved here out of Alerts Strip */}
      {reminder && reminder.message && (
        <div className="mb-3 px-3 py-2 rounded bg-korinth-warning/10 border border-korinth-warning/30 text-[11px] font-mono text-korinth-warning">
          <span className="font-bold">Configuration:</span> {reminder.message}
        </div>
      )}

      <div className="text-[10px] font-mono text-korinth-muted leading-relaxed mb-3">
        Classify each account so the Realized Performance section can filter meaningfully.
        Paper accounts won't count toward real discretionary edge metrics.
      </div>
      <div className="space-y-1.5">
        {accounts.map(acc => (
          <div key={acc.id} className="flex items-center gap-2 bg-korinth-bg rounded px-3 py-2 text-[10px] font-mono">
            <span className="text-white font-bold">{acc.broker}</span>
            <span className="text-korinth-muted">·</span>
            <span className="text-korinth-text">{acc.account_name || acc.external_account_id}</span>
            {acc.external_account_id && <span className="text-[9px] text-korinth-muted/60">({acc.external_account_id})</span>}
            <select
              value={acc.account_purpose || 'unclassified'}
              onChange={e => updatePurpose(acc.id, e.target.value)}
              disabled={saving === acc.id}
              className="ml-auto bg-korinth-surface border border-korinth-border rounded px-2 py-0.5 text-[10px] font-mono text-white disabled:opacity-50"
            >
              {PURPOSE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
